import { c as _c } from "react/compiler-runtime";
import { pinToPositionStyle, pointFromClient, type PinPoint } from "@osmix/ui-primitives";
import { appendImages, applyGroupDiscount, applyProductSelection, discountPercent, IDEA_EDITOR_ACCEPT, IDEA_EDITOR_AUDIENCES, IDEA_EDITOR_DESCRIPTION_MAX, IDEA_EDITOR_MAX_IMAGES, IDEA_EDITOR_TITLE_MAX, IDEA_EDITOR_TYPES, type IdeaEditorImage, type IdeaEditorType, ideaFileProblem, ideaProductIds, movePin, parseDiscountInput, removeImage, removeProduct, replaceImage, setMainImage, updateImagePins } from "@osmix/ui-primitives/idea-editor";
import { Badge } from "@osmix/ui/badge";
import { Button } from "@osmix/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@osmix/ui/dialog";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, ChevronRight, Copy, Download, EyeOff, Heart, ImageIcon, Loader2, RefreshCw, Search, Share, ShoppingBag, Trash2, X } from "lucide-react";
import { type DragEvent, type ReactNode, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router";
import { api, type Idea, type IdeaAudience, type IdeaProductSummary, type Product } from "@/api";
import { ideaKeys } from "@/api/hooks/use-ideas";
import { useStorefronts } from "@/api/hooks/use-storefronts";
import { Select } from "@/components/ui/select";
import { cn, formatPrice } from "@/lib/utils";

// Редактор идеи в консоли. Требование заказчика (встреча 25.09.2026): вид и
// функции создания/редактирования идеи в консоли — те же, что в кабинете
// продавца (apps/seller-web, ideas/_components/IdeaForm). Стеки разные (Next +
// base-ui против Bun SPA + shadcn/Radix), поэтому разметка повторена, а логика
// данных — общая: @osmix/ui-primitives/idea-editor. Внешний вид — в теме
// консоли; структура, поля, шаги и поведение — как в кабинете. Отличия только
// по сути консоли: товары выбираются из всех магазинов с фильтром по магазину
// (у идеи продавца — только из его магазина), своя идея публикуется сразу.

const PLATFORM_SHOP_ID = "shop-osmix";
export const toSummary = (p: Product): IdeaProductSummary => ({
  id: p.id,
  name: p.name,
  article: p.article,
  brand: p.brand,
  photo: p.photos[0],
  color: p.color,
  sizes: p.sizes,
  price: p.price,
  oldPrice: p.oldPrice && p.oldPrice > p.price ? p.oldPrice : undefined,
  shopId: p.shopId,
  status: p.status
});
export function IdeaEditorPage() {
  const $ = _c(20);
  const {
    id
  } = useParams();
  const {
    t
  } = useTranslation();
  const t0 = id ?? "";
  let t1;
  if ($[0] !== t0) {
    t1 = [...ideaKeys.all, "one", t0];
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== id) {
    t2 = () => api.ideas.get(id ?? "");
    $[2] = id;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = !!id;
  let t4;
  if ($[4] !== t1 || $[5] !== t2 || $[6] !== t3) {
    t4 = {
      queryKey: t1,
      queryFn: t2,
      enabled: t3
    };
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const {
    data: idea,
    isLoading,
    error
  } = useQuery(t4);
  if (!id) {
    let t5;
    if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <IdeaEditor />;
      $[8] = t5;
    } else {
      t5 = $[8];
    }
    return t5;
  }
  if (isLoading) {
    let t5;
    if ($[9] !== t) {
      t5 = t("ideas.loading");
      $[9] = t;
      $[10] = t5;
    } else {
      t5 = $[10];
    }
    let t6;
    if ($[11] !== t5) {
      t6 = <p className="text-sm text-muted-foreground">{t5}</p>;
      $[11] = t5;
      $[12] = t6;
    } else {
      t6 = $[12];
    }
    return t6;
  }
  if (error || !idea) {
    let t5;
    if ($[13] !== error || $[14] !== t) {
      t5 = (error as Error | null)?.message ?? t("ideas.editor.notFound");
      $[13] = error;
      $[14] = t;
      $[15] = t5;
    } else {
      t5 = $[15];
    }
    let t6;
    if ($[16] !== t5) {
      t6 = <p className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">{t5}</p>;
      $[16] = t5;
      $[17] = t6;
    } else {
      t6 = $[17];
    }
    return t6;
  }
  let t5;
  if ($[18] !== idea) {
    t5 = <IdeaEditor key={idea.id} initial={idea} />;
    $[18] = idea;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  return t5;
}
function IdeaEditor({
  initial
}: {
  initial?: Idea;
}) {
  const {
    t
  } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const replaceTarget = useRef<string | null>(null);
  const isCreate = !initial;
  // Идея продавца: метки только на товары его магазина (так же проверяет шлюз).
  const lockedShopId = initial && initial.shopId !== PLATFORM_SHOP_ID && initial.shopId !== "osmix" ? initial.shopId : undefined;
  const [step, setStep] = useState<"edit" | "preview">("edit");
  const [title, setTitle] = useState(initial?.title ?? "");
  const [description, setDescription] = useState(initial?.description ?? "");
  const [audience, setAudience] = useState<IdeaAudience[]>(initial?.audience ?? []);
  const [type, setType] = useState<IdeaEditorType | null>(initial?.type as IdeaEditorType | undefined ?? null);
  const [images, setImages] = useState<IdeaEditorImage[]>(initial?.images ?? []);
  const [discountOn, setDiscountOn] = useState(!!initial?.groupDiscount);
  const [discountUnit, setDiscountUnit] = useState<"rub" | "percent">(initial?.groupDiscount?.unit ?? "rub");
  const [discountRaw, setDiscountRaw] = useState(initial?.groupDiscount ? String(initial.groupDiscount.value) : "");
  const [known, setKnown] = useState<IdeaProductSummary[]>(initial?.products ?? []);
  const [picker, setPicker] = useState<{
    at?: PinPoint;
  } | null>(null);
  const [hovered, setHovered] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const main = images[0] ?? null;
  const productIds = ideaProductIds(images);
  const products = productIds.map(pid => known.find(p => p.id === pid)).filter((p_0): p_0 is IdeaProductSummary => !!p_0);
  const discount = discountOn ? parseDiscountInput(discountRaw, discountUnit) : null;
  const total = products.reduce((s, p_1) => s + p_1.price, 0);
  const discounted = applyGroupDiscount(total, discount);
  const hasPhoto = images.length > 0;
  const onFiles = async (list: FileList | null) => {
    const files = Array.from(list ?? []);
    if (fileRef.current) fileRef.current.value = "";
    if (!files.length) return;
    const target = replaceTarget.current;
    replaceTarget.current = null;
    setUploading(true);
    setError(null);
    try {
      const urls: string[] = [];
      const room = target ? 1 : IDEA_EDITOR_MAX_IMAGES - images.length;
      for (const file of files.slice(0, Math.max(0, room))) {
        const problem = ideaFileProblem(file);
        if (problem) {
          setError(t(problem === "type" ? "ideas.editor.fileType" : "ideas.editor.fileSize"));
          continue;
        }
        urls.push(await api.ideas.uploadPhoto(file));
      }
      if (target && urls[0]) setImages(cur => replaceImage(cur, target, urls[0] ?? ""));else if (urls.length) setImages(cur_0 => appendImages(cur_0, urls));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setUploading(false);
    }
  };
  const submit = async () => {
    if (!title.trim()) {
      setStep("edit");
      setError(t("ideas.editor.noTitle"));
      return;
    }
    if (!hasPhoto) return;
    const body = {
      title: title.trim(),
      description: description.trim(),
      type: type ?? "inspiration",
      images,
      audience,
      groupDiscount: discount
    };
    setSaving(true);
    setError(null);
    try {
      if (initial) await api.ideas.update(initial.id, body);else await api.ideas.createPlatform(body);
      await queryClient.invalidateQueries({
        queryKey: ideaKeys.all
      });
      navigate(`/ideas?saved=${isCreate ? "created" : "updated"}`);
    } catch (e_0) {
      setError((e_0 as Error).message);
    } finally {
      setSaving(false);
    }
  };
  const pageTitle = t(isCreate ? "ideas.editor.createTitle" : "ideas.editor.editTitle");
  return <div className="mx-auto flex w-full max-w-[1144px] flex-col gap-6">
			<div className="flex items-center justify-between gap-4">
				<h1 className="flex flex-wrap items-center gap-3 text-xl font-bold" data-tour="idea-editor-intro">
					{step === "preview" ? <>
							<button type="button" onClick={() => setStep("edit")} className="hover:underline">
								{pageTitle}
							</button>
							<ChevronRight className="size-5" />
							{t("ideas.editor.previewTitle")}
						</> : pageTitle}
					{initial && <Badge variant="outline" className="font-normal">
							{initial.shopName ?? initial.shopId}
						</Badge>}
				</h1>
				<Button asChild variant="ghost" size="icon" aria-label={t("ideas.editor.close")}>
					<Link to="/ideas">
						<X className="size-5" />
					</Link>
				</Button>
			</div>

			{error && <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
					{error}
				</div>}

			<div className="rounded-xl border bg-card p-5">
				{step === "preview" ? <IdeaPreview title={title.trim()} description={description.trim()} images={images} products={products} discount={discount} /> : <div className="grid items-start gap-5 lg:grid-cols-2">
						<PhotoCanvas images={images} hovered={hovered} setHovered={setHovered} productById={pid_0 => known.find(p_2 => p_2.id === pid_0)} uploading={uploading} fileRef={fileRef} onFiles={onFiles} onAdd={() => {
          replaceTarget.current = null;
          fileRef.current?.click();
        }} onReplace={imgId => {
          replaceTarget.current = imgId;
          fileRef.current?.click();
        }} onRemove={imgId_0 => setImages(cur_1 => removeImage(cur_1, imgId_0))} onMakeMain={imgId_1 => setImages(cur_2 => setMainImage(cur_2, imgId_1))} onOpenPicker={at => setPicker({
          at
        })} onMovePin={(pinId, point) => main && setImages(cur_3 => updateImagePins(cur_3, main.id, pins => movePin(pins, pinId, point)))} />

						<div className="flex flex-col">
							<Field label={t("ideas.editor.titleLabel")} help={t("ideas.editor.titleHelp")}>
								<input value={title} maxLength={IDEA_EDITOR_TITLE_MAX} onChange={e_1 => setTitle(e_1.target.value)} placeholder={t("ideas.editor.inputPlaceholder")} data-tour="idea-editor-title" className="h-10 w-full rounded-md border border-input bg-muted/40 px-3 text-sm outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50" />
								<Counter value={title.length} max={IDEA_EDITOR_TITLE_MAX} />
							</Field>
							<Field label={t("ideas.editor.descriptionLabel")} help={t("ideas.editor.descriptionHelp")}>
								<textarea value={description} maxLength={IDEA_EDITOR_DESCRIPTION_MAX} onChange={e_2 => setDescription(e_2.target.value)} placeholder={t("ideas.editor.inputPlaceholder")} className="h-[104px] w-full resize-none rounded-md border border-input bg-muted/40 px-3 py-2.5 text-sm outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50" />
								<Counter value={description.length} max={IDEA_EDITOR_DESCRIPTION_MAX} />
							</Field>
							<Field label={t("ideas.editor.audienceLabel")} help={t("ideas.editor.audienceHelp")}>
								<div className="flex flex-wrap gap-3" data-tour="idea-editor-audience">
									{IDEA_EDITOR_AUDIENCES.map(a => <Chip key={a} active={audience.includes(a)} onClick={() => setAudience(cur_4 => cur_4.includes(a) ? cur_4.filter(x => x !== a) : [...cur_4, a])}>
											{t(`ideas.editor.audience.${a}`)}
										</Chip>)}
								</div>
							</Field>
							<Field label={t("ideas.editor.typeLabel")} help={t("ideas.editor.typeHelp")}>
								<div className="flex flex-wrap gap-3">
									{IDEA_EDITOR_TYPES.map(it => <Chip key={it} active={type === it} onClick={() => setType(it)}>
											{t(`ideas.editor.type.${it}`)}
										</Chip>)}
								</div>
							</Field>

							<div className="flex flex-col gap-2" data-tour="idea-editor-products">
								<Label>{t("ideas.editor.productsLabel")}</Label>
								<p className="text-sm">
									{hasPhoto ? t("ideas.editor.productsHint") : t("ideas.editor.productsHintNoPhoto")}
								</p>
							</div>

							<div className="mt-4 flex flex-col gap-4">
								{productIds.length > 0 && <div className="flex max-h-[520px] flex-col gap-2 overflow-y-auto">
										{productIds.map(pid_1 => <div key={pid_1} onPointerEnter={() => setHovered(pid_1)} onPointerLeave={() => setHovered(null)}>
												<ProductCard product={known.find(p_3 => p_3.id === pid_1)} fallbackId={pid_1} highlighted={hovered === pid_1} actions={<button type="button" aria-label={t("ideas.editor.removePin")} onClick={() => setImages(cur_5 => removeProduct(cur_5, pid_1))} className="absolute right-3 top-3 flex size-6 items-center justify-center rounded-full hover:bg-background">
															<X className="size-4" />
														</button>} />
											</div>)}
									</div>}

								{products.length > 0 && <div className="flex flex-col gap-4">
										<label className="flex w-fit cursor-pointer items-center gap-2 text-sm">
											<button type="button" role="switch" aria-checked={discountOn} onClick={() => setDiscountOn(v => !v)} className={cn("relative h-5 w-9 rounded-full transition-colors", discountOn ? "bg-primary" : "bg-muted-foreground/30")}>
												<span className={cn("absolute top-0.5 size-4 rounded-full bg-white transition-all", discountOn ? "left-[18px]" : "left-0.5")} />
											</button>
											{t("ideas.editor.discountToggle")}
										</label>
										{discountOn && <div className="flex flex-col gap-4">
												<p className="text-sm">{t("ideas.editor.discountHint")}</p>
												<div className="flex items-center gap-5">
													<input inputMode="decimal" value={discountRaw} onChange={e_3 => setDiscountRaw(e_3.target.value.replace(/[^\d\s.,]/g, ""))} placeholder={discountUnit === "rub" ? "2 000" : "10"} aria-label={t("ideas.editor.discountValue")} className="h-10 w-[100px] rounded-md border border-input bg-muted/40 px-3 text-sm outline-none" />
													<div className="flex items-center gap-1" role="radiogroup">
														{(["rub", "percent"] as const).map(u => <button key={u} type="button" role="radio" aria-checked={discountUnit === u} onClick={() => setDiscountUnit(u)} className={cn("flex h-[25px] min-w-[35px] items-center justify-center rounded-full px-2 text-sm", discountUnit === u ? "bg-primary text-primary-foreground" : "hover:bg-muted")}>
																{u === "rub" ? "₽" : "%"}
															</button>)}
													</div>
												</div>
											</div>}
										<p className="flex items-baseline gap-3 font-medium">
											{t("ideas.editor.total")}
											<span className="font-normal">{formatPrice(discounted)}</span>
											{discounted !== total && <span className="text-xs font-normal text-muted-foreground line-through">
													{formatPrice(total)}
												</span>}
										</p>
									</div>}

								<div className="flex flex-wrap gap-3" data-tour="idea-editor-actions">
									<Button disabled={!hasPhoto || saving} onClick={() => void submit()}>
										{saving ? t("ideas.editor.saving") : t(isCreate ? "ideas.editor.publish" : "ideas.editor.save")}
									</Button>
									<Button variant="secondary" disabled={!hasPhoto} onClick={() => setStep("preview")}>
										{t("ideas.editor.preview")}
									</Button>
								</div>
							</div>
						</div>
					</div>}
			</div>

			{step === "preview" && <div className="flex flex-wrap gap-3">
					<Button disabled={saving} onClick={() => void submit()}>
						{saving ? t("ideas.editor.saving") : t(isCreate ? "ideas.editor.publish" : "ideas.editor.save")}
					</Button>
					<Button variant="secondary" onClick={() => setStep("edit")}>
						{t("ideas.editor.backToEdit")}
					</Button>
				</div>}

			<ProductPickerDialog open={!!picker} lockedShopId={lockedShopId} selectedIds={main ? main.pins.map(p_4 => p_4.productId) : []} onClose={() => setPicker(null)} onConfirm={(ids, picked) => {
      setKnown(cur_6 => [...cur_6.filter(p_5 => !picked.some(x_0 => x_0.id === p_5.id)), ...picked]);
      if (main) setImages(cur_7 => updateImagePins(cur_7, main.id, pins_0 => applyProductSelection(pins_0, ids, picker?.at)));
      setPicker(null);
    }} />
		</div>;
}
function Label(t0) {
  const $ = _c(5);
  const {
    children,
    help
  } = t0;
  let t1;
  if ($[0] !== help) {
    t1 = help && <span title={help} className="inline-flex size-[18px] cursor-help items-center justify-center rounded-full bg-muted text-[11px] font-semibold text-muted-foreground">?</span>;
    $[0] = help;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = <span className="flex items-center gap-1.5 text-sm font-medium">{children}{t1}</span>;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
function Field(t0) {
  const $ = _c(6);
  const {
    label,
    help,
    children
  } = t0;
  let t1;
  if ($[0] !== help || $[1] !== label) {
    t1 = <Label help={help}>{label}</Label>;
    $[0] = help;
    $[1] = label;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== children || $[4] !== t1) {
    t2 = <div className="mb-6 flex flex-col gap-2">{t1}{children}</div>;
    $[3] = children;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
}
function Counter(t0) {
  const $ = _c(6);
  const {
    value,
    max
  } = t0;
  const {
    t
  } = useTranslation();
  let t1;
  if ($[0] !== max || $[1] !== t || $[2] !== value) {
    t1 = t("ideas.editor.counter", {
      value,
      max
    });
    $[0] = max;
    $[1] = t;
    $[2] = value;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== t1) {
    t2 = <span className="text-xs text-muted-foreground">{t1}</span>;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
}
function Chip(t0) {
  const $ = _c(7);
  const {
    active,
    onClick,
    children
  } = t0;
  const t1 = active ? "border-primary bg-primary/10" : "border-transparent bg-muted hover:border-border";
  let t2;
  if ($[0] !== t1) {
    t2 = cn("h-10 rounded-full border px-4 text-sm transition-colors", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== active || $[3] !== children || $[4] !== onClick || $[5] !== t2) {
    t3 = <button type="button" aria-pressed={active} onClick={onClick} className={t2}>{children}</button>;
    $[2] = active;
    $[3] = children;
    $[4] = onClick;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}
function PriceLine(t0) {
  const $ = _c(13);
  const {
    price,
    oldPrice
  } = t0;
  let t1;
  if ($[0] !== oldPrice || $[1] !== price) {
    t1 = discountPercent(price, oldPrice);
    $[0] = oldPrice;
    $[1] = price;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const pct = t1;
  let t2;
  if ($[3] !== price) {
    t2 = formatPrice(price);
    $[3] = price;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t2) {
    t3 = <span>{t2}</span>;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== oldPrice || $[8] !== pct) {
    t4 = pct !== null && oldPrice && <><span className="text-xs text-muted-foreground line-through">{formatPrice(oldPrice)}</span><Badge className="px-1.5 py-0 text-[11px]">-{pct}%</Badge></>;
    $[7] = oldPrice;
    $[8] = pct;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t3 || $[11] !== t4) {
    t5 = <span className="flex flex-wrap items-center gap-2">{t3}{t4}</span>;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  return t5;
}
function ArticleCopy(t0) {
  const $ = _c(11);
  const {
    article
  } = t0;
  const {
    t
  } = useTranslation();
  let t1;
  if ($[0] !== t) {
    t1 = t("ideas.editor.copyArticle");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== article) {
    t2 = e => {
      e.stopPropagation();
      navigator.clipboard?.writeText(article).catch(_temp);
    };
    $[2] = article;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Copy className="size-4" />;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t1 || $[6] !== t2) {
    t4 = <button type="button" aria-label={t1} onClick={t2} className="relative z-10 rounded p-0.5 hover:bg-background">{t3}</button>;
    $[5] = t1;
    $[6] = t2;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== article || $[9] !== t4) {
    t5 = <span className="flex items-center gap-1.5 text-sm">{article}{t4}</span>;
    $[8] = article;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
}

// Карточка товара идеи — как в кабинете: фото 80×107, название, артикул с
// копированием, цвет, цена со старой и скидкой.
function _temp() {}
export function ProductCard(t0) {
  const $ = _c(23);
  const {
    product,
    fallbackId,
    highlighted,
    actions,
    className
  } = t0;
  const t1 = highlighted ? "border-primary bg-primary/5" : "border-transparent bg-muted/60";
  let t2;
  if ($[0] !== className || $[1] !== t1) {
    t2 = cn("relative flex min-h-[123px] gap-3 rounded-xl border p-2 transition-colors", t1, className);
    $[0] = className;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== product) {
    t3 = <span className="relative h-[107px] w-[80px] shrink-0 overflow-hidden rounded-lg bg-background">{product?.photo ? <img src={product.photo} alt="" className="size-full object-cover" /> : <span className="flex size-full items-center justify-center text-muted-foreground"><ImageIcon className="size-5" /></span>}</span>;
    $[3] = product;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const t4 = product?.name ?? fallbackId;
  let t5;
  if ($[5] !== t4) {
    t5 = <span className="line-clamp-1">{t4}</span>;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== product) {
    t6 = product && <ArticleCopy article={product.article} />;
    $[7] = product;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== product) {
    t7 = product?.color && <span className="text-xs text-muted-foreground">{product.color}</span>;
    $[9] = product;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== product) {
    t8 = product && <span className="mt-auto"><PriceLine price={product.price} oldPrice={product.oldPrice} /></span>;
    $[11] = product;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== t5 || $[14] !== t6 || $[15] !== t7 || $[16] !== t8) {
    t9 = <span className="flex min-w-0 flex-1 flex-col gap-2 py-1 pr-6 text-sm">{t5}{t6}{t7}{t8}</span>;
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== actions || $[19] !== t2 || $[20] !== t3 || $[21] !== t9) {
    t10 = <div className={t2}>{t3}{t9}{actions}</div>;
    $[18] = actions;
    $[19] = t2;
    $[20] = t3;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  return t10;
}
const DRAG_TYPE = "application/x-osmix-idea-image";
function PhotoCanvas(t0) {
  const $ = _c(54);
  const {
    images,
    hovered,
    setHovered,
    productById,
    uploading,
    fileRef,
    onFiles,
    onAdd,
    onReplace,
    onRemove,
    onMakeMain,
    onOpenPicker,
    onMovePin
  } = t0;
  const {
    t
  } = useTranslation();
  const main = images[0] ?? null;
  const frameRef = useRef(null);
  const drag = useRef(null);
  const [ratio, setRatio] = useState(0.75);
  const [dropping, setDropping] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = (x, y) => {
      const rect = frameRef.current?.getBoundingClientRect();
      return rect ? pointFromClient({
        clientX: x,
        clientY: y
      }, rect) : {
        x: 50,
        y: 50
      };
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const pct = t1;
  const hoveredPin = main?.pins.find(p => p.productId === hovered);
  const atLimit = images.length >= IDEA_EDITOR_MAX_IMAGES;
  let t2;
  if ($[1] !== onMakeMain) {
    t2 = e => {
      e.preventDefault();
      setDropping(false);
      const id = e.dataTransfer.getData(DRAG_TYPE);
      if (id) {
        onMakeMain(id);
      }
    };
    $[1] = onMakeMain;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const onDrop = t2;
  let t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = e_0 => {
      if (e_0.dataTransfer.types.includes(DRAG_TYPE)) {
        e_0.preventDefault();
        setDropping(true);
      }
    };
    t4 = () => setDropping(false);
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  let t5;
  if ($[5] !== onDrop) {
    t5 = {
      onDragOver: t3,
      onDragLeave: t4,
      onDrop
    };
    $[5] = onDrop;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const drop = t5;
  const t6 = "flex flex-col";
  const t7 = "idea-editor-photo";
  let t8;
  if ($[7] !== onFiles) {
    t8 = e_1 => onFiles(e_1.target.files);
    $[7] = onFiles;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] !== fileRef || $[10] !== t8) {
    t9 = <input ref={fileRef} type="file" accept={IDEA_EDITOR_ACCEPT} multiple={true} className="hidden" onChange={t8} />;
    $[9] = fileRef;
    $[10] = t8;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== t) {
    t10 = t("ideas.editor.photoLabel");
    $[12] = t;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] !== t10) {
    t11 = <Label>{t10}</Label>;
    $[14] = t10;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] !== t) {
    t12 = t("ideas.editor.photoHint");
    $[16] = t;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  let t13;
  if ($[18] !== t12) {
    t13 = <p className="mt-2 text-sm">{t12}</p>;
    $[18] = t12;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  const t14 = uploading || atLimit;
  let t15;
  if ($[20] !== uploading) {
    t15 = uploading && <Loader2 className="size-4 animate-spin" />;
    $[20] = uploading;
    $[21] = t15;
  } else {
    t15 = $[21];
  }
  let t16;
  if ($[22] !== t || $[23] !== uploading) {
    t16 = uploading ? t("ideas.editor.uploading") : t("ideas.editor.uploadPhoto");
    $[22] = t;
    $[23] = uploading;
    $[24] = t16;
  } else {
    t16 = $[24];
  }
  let t17;
  if ($[25] !== onAdd || $[26] !== t14 || $[27] !== t15 || $[28] !== t16) {
    t17 = <Button variant="secondary" onClick={onAdd} disabled={t14}>{t15}{t16}</Button>;
    $[25] = onAdd;
    $[26] = t14;
    $[27] = t15;
    $[28] = t16;
    $[29] = t17;
  } else {
    t17 = $[29];
  }
  let t18;
  if ($[30] !== t) {
    t18 = t("ideas.editor.fileHint");
    $[30] = t;
    $[31] = t18;
  } else {
    t18 = $[31];
  }
  let t19;
  if ($[32] !== t18) {
    t19 = <span className="text-xs text-muted-foreground">{t18}</span>;
    $[32] = t18;
    $[33] = t19;
  } else {
    t19 = $[33];
  }
  let t20;
  if ($[34] !== t17 || $[35] !== t19) {
    t20 = <div className="mt-2.5 flex items-center gap-2">{t17}{t19}</div>;
    $[34] = t17;
    $[35] = t19;
    $[36] = t20;
  } else {
    t20 = $[36];
  }
  const t21 = "mt-4";
  const t22 = main ? <div ref={frameRef} {...drop} className={cn("relative w-full overflow-hidden rounded-md bg-muted", dropping && "ring-2 ring-primary")} style={{
    aspectRatio: ratio
  }}><img src={main.url} alt="" draggable={false} onLoad={e_2 => {
      const img = e_2.currentTarget;
      if (img.naturalWidth && img.naturalHeight) {
        setRatio(img.naturalWidth / img.naturalHeight);
      }
    }} className="size-full object-cover" /><button type="button" aria-label={t("ideas.editor.choosePin")} onClick={e_3 => onOpenPicker(pct(e_3.clientX, e_3.clientY))} className="absolute inset-0 cursor-crosshair" /><span className="pointer-events-none absolute right-1 top-1 rounded-md bg-background px-1.5 py-1 text-sm">{t("ideas.editor.mainPhoto")}</span>{main.pins.map(pin => <button key={pin.id} type="button" aria-label={productById(pin.productId)?.name ?? t("ideas.editor.choosePin")} style={{
      ...pinToPositionStyle(pin),
      touchAction: "none"
    }} onPointerEnter={() => setHovered(pin.productId)} onPointerLeave={() => setHovered(null)} onPointerDown={e_4 => {
      e_4.stopPropagation();
      e_4.currentTarget.setPointerCapture(e_4.pointerId);
      drag.current = {
        id: pin.id,
        startX: e_4.clientX,
        startY: e_4.clientY,
        moved: false
      };
    }} onPointerMove={e_5 => {
      const st = drag.current;
      if (!st || st.id !== pin.id) {
        return;
      }
      if (!st.moved && Math.hypot(e_5.clientX - st.startX, e_5.clientY - st.startY) < 4) {
        return;
      }
      st.moved = true;
      onMovePin(pin.id, pct(e_5.clientX, e_5.clientY));
    }} onPointerUp={e_6 => {
      e_6.stopPropagation();
      const st_0 = drag.current;
      drag.current = null;
      if (st_0 && !st_0.moved) {
        onOpenPicker();
      }
    }} className={cn("absolute z-10 flex size-5 cursor-grab items-center justify-center rounded-full bg-white p-[2px] shadow transition-transform", hovered === pin.productId && "scale-125")}><span className="size-full rounded-full bg-primary" /></button>)}{hoveredPin && <div className="pointer-events-none absolute z-20 w-[276px] max-w-[80%] -translate-x-1/2 -translate-y-[calc(100%+14px)]" style={{
      left: `${hoveredPin.x}%`,
      top: `${hoveredPin.y}%`
    }}><ProductCard product={productById(hoveredPin.productId)} fallbackId={hoveredPin.productId} className="bg-background shadow-lg" /></div>}<PhotoActions onReplace={() => onReplace(main.id)} onRemove={() => onRemove(main.id)} /></div> : <button type="button" onClick={onAdd} disabled={uploading} className="flex aspect-[3/4] w-full flex-col items-center justify-center gap-3 rounded-lg border border-dashed px-6 text-center hover:border-foreground/40"><ImageIcon className="size-10 text-muted-foreground" strokeWidth={1.25} /><span className="text-sm font-medium">{t("ideas.editor.mainPhoto")}</span><span className="max-w-[260px] text-sm text-muted-foreground">{t("ideas.editor.mainPhotoHint")}</span></button>;
  let t23;
  if ($[37] !== t22) {
    t23 = <div className={t21}>{t22}</div>;
    $[37] = t22;
    $[38] = t23;
  } else {
    t23 = $[38];
  }
  let t24;
  if ($[39] !== images || $[40] !== onAdd || $[41] !== onMakeMain || $[42] !== onRemove || $[43] !== onReplace || $[44] !== ratio || $[45] !== t) {
    t24 = <div className="mt-1.5 grid grid-cols-4 gap-1.5">{Array.from({
        length: IDEA_EDITOR_MAX_IMAGES
      }, (_, i) => {
        const img_0 = images[i];
        if (!img_0) {
          return <button key={`slot-${i}`} type="button" onClick={onAdd} aria-label={t("ideas.editor.uploadPhoto")} className="rounded-md border border-dashed hover:border-foreground/40" style={{
            aspectRatio: ratio
          }} />;
        }
        return <div key={img_0.id} draggable={true} onDragStart={e_7 => {
          e_7.dataTransfer.setData(DRAG_TYPE, img_0.id);
          e_7.dataTransfer.effectAllowed = "move";
        }} className="group/thumb relative cursor-grab overflow-hidden rounded-md bg-muted" style={{
          aspectRatio: ratio
        }}><button type="button" aria-label={t("ideas.editor.makeMain")} onClick={() => onMakeMain(img_0.id)} className="absolute inset-0"><img src={img_0.url} alt="" draggable={false} className="size-full object-cover" /></button><PhotoActions small={true} onReplace={() => onReplace(img_0.id)} onRemove={() => onRemove(img_0.id)} /></div>;
      })}</div>;
    $[39] = images;
    $[40] = onAdd;
    $[41] = onMakeMain;
    $[42] = onRemove;
    $[43] = onReplace;
    $[44] = ratio;
    $[45] = t;
    $[46] = t24;
  } else {
    t24 = $[46];
  }
  let t25;
  if ($[47] !== t11 || $[48] !== t13 || $[49] !== t20 || $[50] !== t23 || $[51] !== t24 || $[52] !== t9) {
    t25 = <div className={t6} data-tour={t7}>{t9}{t11}{t13}{t20}{t23}{t24}</div>;
    $[47] = t11;
    $[48] = t13;
    $[49] = t20;
    $[50] = t23;
    $[51] = t24;
    $[52] = t9;
    $[53] = t25;
  } else {
    t25 = $[53];
  }
  return t25;
}
function PhotoActions(t0) {
  const $ = _c(18);
  const {
    onReplace,
    onRemove,
    small
  } = t0;
  const {
    t
  } = useTranslation();
  const t1 = small && "opacity-0 transition-opacity group-hover/thumb:opacity-100 focus-within:opacity-100";
  let t2;
  if ($[0] !== t1) {
    t2 = cn("absolute bottom-1 right-1 z-10 flex gap-1", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== t) {
    t3 = t("ideas.editor.replacePhoto");
    $[2] = t;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <RefreshCw className="size-4" />;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== onReplace || $[6] !== t3) {
    t5 = <button type="button" aria-label={t3} onClick={onReplace} className="flex size-[30px] items-center justify-center rounded-md bg-background shadow-sm hover:bg-muted">{t4}</button>;
    $[5] = onReplace;
    $[6] = t3;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t) {
    t6 = t("ideas.editor.removePhoto");
    $[8] = t;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Trash2 className="size-4" />;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== onRemove || $[12] !== t6) {
    t8 = <button type="button" aria-label={t6} onClick={onRemove} className="flex size-[30px] items-center justify-center rounded-md bg-background shadow-sm hover:bg-muted">{t7}</button>;
    $[11] = onRemove;
    $[12] = t6;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== t2 || $[15] !== t5 || $[16] !== t8) {
    t9 = <div className={t2}>{t5}{t8}</div>;
    $[14] = t2;
    $[15] = t5;
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  return t9;
}

// «Добавить товар»: та же модалка, что в кабинете, но товары — из всех магазинов
// с фильтром по магазину. Для идеи продавца магазин зафиксирован.
function ProductPickerDialog(t0) {
  const $ = _c(129);
  const {
    open,
    lockedShopId,
    selectedIds,
    onClose,
    onConfirm
  } = t0;
  const {
    t
  } = useTranslation();
  const [q, setQ] = useState("");
  const [shopId, setShopId] = useState(lockedShopId ?? "");
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [draft, setDraft] = useState(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const [picked, setPicked] = useState(t2);
  const {
    data: storefronts
  } = useStorefronts();
  let t3;
  if ($[2] !== lockedShopId || $[3] !== open || $[4] !== selectedIds) {
    t3 = () => {
      if (!open) {
        return;
      }
      setDraft(selectedIds);
      setQ("");
      setShopId(lockedShopId ?? "");
    };
    $[2] = lockedShopId;
    $[3] = open;
    $[4] = selectedIds;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== open) {
    t4 = [open];
    $[6] = open;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[8] !== q || $[9] !== shopId) {
    t5 = ["products", "idea-picker", q, shopId];
    t6 = () => api.products.list({
      status: "published",
      page: 1,
      pageSize: 60,
      search: q.trim() || undefined,
      shopId: shopId || undefined
    });
    $[8] = q;
    $[9] = shopId;
    $[10] = t5;
    $[11] = t6;
  } else {
    t5 = $[10];
    t6 = $[11];
  }
  let t7;
  if ($[12] !== open || $[13] !== t5 || $[14] !== t6) {
    t7 = {
      queryKey: t5,
      queryFn: t6,
      enabled: open
    };
    $[12] = open;
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const {
    data
  } = useQuery(t7);
  let t8;
  if ($[16] !== data?.data) {
    t8 = data?.data ?? [];
    $[16] = data?.data;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const products = t8;
  let t9;
  if ($[18] !== draft) {
    t9 = [...draft].sort().join(",");
    $[18] = draft;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t9) {
    t10 = ["products", "idea-picker-selected", t9];
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== draft) {
    t11 = async () => (await Promise.all(draft.map(_temp3))).filter(_temp4);
    $[22] = draft;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  const t12 = open && draft.length > 0;
  let t13;
  if ($[24] !== t10 || $[25] !== t11 || $[26] !== t12) {
    t13 = {
      queryKey: t10,
      queryFn: t11,
      enabled: t12
    };
    $[24] = t10;
    $[25] = t11;
    $[26] = t12;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  const {
    data: selectedFetched
  } = useQuery(t13);
  let t14;
  if ($[28] !== picked || $[29] !== products || $[30] !== selectedFetched) {
    t14 = id_0 => {
      const p_0 = products.find(x_0 => x_0.id === id_0) ?? selectedFetched?.find(x => x.id === id_0);
      return p_0 ? toSummary(p_0) : picked.find(x_1 => x_1.id === id_0);
    };
    $[28] = picked;
    $[29] = products;
    $[30] = selectedFetched;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  const summaryOf = t14;
  let t15;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = p_1 => {
      setPicked(cur => cur.some(x_2 => x_2.id === p_1.id) ? cur : [...cur, toSummary(p_1)]);
      setDraft(d => d.includes(p_1.id) ? d.filter(x_3 => x_3 !== p_1.id) : [...d, p_1.id]);
    };
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  const toggle = t15;
  let t16;
  if ($[33] !== onClose) {
    t16 = o => !o && onClose();
    $[33] = onClose;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== t) {
    t17 = t("ideas.picker.title");
    $[35] = t;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  let t18;
  if ($[37] !== t17) {
    t18 = <DialogHeader className="border-b px-4 py-3.5"><DialogTitle>{t17}</DialogTitle></DialogHeader>;
    $[37] = t17;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  let t19;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />;
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  let t20;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = e => setQ(e.target.value);
    $[40] = t20;
  } else {
    t20 = $[40];
  }
  let t21;
  if ($[41] !== t) {
    t21 = t("ideas.picker.search");
    $[41] = t;
    $[42] = t21;
  } else {
    t21 = $[42];
  }
  let t22;
  if ($[43] !== q || $[44] !== t21) {
    t22 = <div className="relative w-full max-w-[276px]">{t19}<input value={q} onChange={t20} placeholder={t21} className="h-10 w-full rounded-full border border-input bg-muted/40 pl-9 pr-3 text-sm outline-none" /></div>;
    $[43] = q;
    $[44] = t21;
    $[45] = t22;
  } else {
    t22 = $[45];
  }
  const t23 = !!lockedShopId;
  let t24;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = e_0 => setShopId(e_0.target.value);
    $[46] = t24;
  } else {
    t24 = $[46];
  }
  let t25;
  if ($[47] !== t) {
    t25 = t("ideas.picker.shop");
    $[47] = t;
    $[48] = t25;
  } else {
    t25 = $[48];
  }
  let t26;
  if ($[49] !== t) {
    t26 = t("ideas.picker.allShops");
    $[49] = t;
    $[50] = t26;
  } else {
    t26 = $[50];
  }
  let t27;
  if ($[51] !== t26) {
    t27 = <option value="">{t26}</option>;
    $[51] = t26;
    $[52] = t27;
  } else {
    t27 = $[52];
  }
  let t28;
  if ($[53] !== storefronts) {
    t28 = storefronts ?? [];
    $[53] = storefronts;
    $[54] = t28;
  } else {
    t28 = $[54];
  }
  let t29;
  if ($[55] !== t28) {
    t29 = t28.map(_temp5);
    $[55] = t28;
    $[56] = t29;
  } else {
    t29 = $[56];
  }
  let t30;
  if ($[57] !== shopId || $[58] !== t23 || $[59] !== t25 || $[60] !== t27 || $[61] !== t29) {
    t30 = <Select value={shopId} disabled={t23} onChange={t24} aria-label={t25} className="min-w-[200px]">{t27}{t29}</Select>;
    $[57] = shopId;
    $[58] = t23;
    $[59] = t25;
    $[60] = t27;
    $[61] = t29;
    $[62] = t30;
  } else {
    t30 = $[62];
  }
  let t31;
  if ($[63] !== t22 || $[64] !== t30) {
    t31 = <div className="flex flex-wrap items-center justify-between gap-3">{t22}{t30}</div>;
    $[63] = t22;
    $[64] = t30;
    $[65] = t31;
  } else {
    t31 = $[65];
  }
  let t32;
  if ($[66] !== draft || $[67] !== products || $[68] !== t) {
    t32 = <div className="min-h-0 flex-1 overflow-y-auto">{products.length === 0 ? <p className="py-10 text-center text-sm text-muted-foreground">{t("ideas.picker.empty")}</p> : <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">{products.map(p_2 => {
          const selected = draft.includes(p_2.id);
          const s_0 = toSummary(p_2);
          return <div key={p_2.id} className={cn("relative flex min-h-[123px] gap-3 rounded-xl border bg-muted/60 p-2", selected ? "border-primary" : "border-transparent")}><button type="button" aria-pressed={selected} aria-label={p_2.name} onClick={() => toggle(p_2)} className="absolute inset-0 z-0 rounded-xl" /><span className="pointer-events-none relative h-[107px] w-[80px] shrink-0 overflow-hidden rounded-lg bg-background">{s_0.photo && <img src={s_0.photo} alt="" className="size-full object-cover" />}<span className={cn("absolute left-1.5 top-1.5 flex size-[19px] items-center justify-center rounded-md border", selected ? "border-primary bg-primary text-primary-foreground" : "bg-background")}>{selected && <Check className="size-3" strokeWidth={3} />}</span></span><span className="pointer-events-none relative flex min-w-0 flex-1 flex-col gap-2 py-1 text-sm"><span className="line-clamp-1">{p_2.name}</span><span className="pointer-events-auto w-fit"><ArticleCopy article={p_2.article} /></span><span className="text-xs text-muted-foreground">{[p_2.color, p_2.sizes.length === 1 ? p_2.sizes[0] : null].filter(Boolean).join(", ")}</span><span className="mt-auto"><PriceLine price={s_0.price} oldPrice={s_0.oldPrice} /></span></span></div>;
        })}</div>}</div>;
    $[66] = draft;
    $[67] = products;
    $[68] = t;
    $[69] = t32;
  } else {
    t32 = $[69];
  }
  let t33;
  if ($[70] !== t31 || $[71] !== t32) {
    t33 = <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-3 p-3">{t31}{t32}</div>;
    $[70] = t31;
    $[71] = t32;
    $[72] = t33;
  } else {
    t33 = $[72];
  }
  let t34;
  if ($[73] !== t) {
    t34 = t("ideas.picker.selected");
    $[73] = t;
    $[74] = t34;
  } else {
    t34 = $[74];
  }
  let t35;
  if ($[75] !== t34) {
    t35 = <span className="text-sm font-medium">{t34}</span>;
    $[75] = t34;
    $[76] = t35;
  } else {
    t35 = $[76];
  }
  let t36;
  if ($[77] !== draft || $[78] !== summaryOf || $[79] !== t) {
    let t37;
    if ($[81] !== summaryOf || $[82] !== t) {
      t37 = id_1 => <ProductCard key={id_1} product={summaryOf(id_1)} fallbackId={id_1} className="bg-background" actions={<button type="button" aria-label={t("ideas.picker.remove")} onClick={() => setDraft(d_0 => d_0.filter(x_4 => x_4 !== id_1))} className="absolute right-2 top-2 flex size-6 items-center justify-center rounded-full hover:bg-muted"><X className="size-4" /></button>} />;
      $[81] = summaryOf;
      $[82] = t;
      $[83] = t37;
    } else {
      t37 = $[83];
    }
    t36 = draft.map(t37);
    $[77] = draft;
    $[78] = summaryOf;
    $[79] = t;
    $[80] = t36;
  } else {
    t36 = $[80];
  }
  let t37;
  if ($[84] !== t36) {
    t37 = <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto">{t36}</div>;
    $[84] = t36;
    $[85] = t37;
  } else {
    t37 = $[85];
  }
  let t38;
  if ($[86] !== t35 || $[87] !== t37) {
    t38 = <aside className="hidden w-[300px] shrink-0 flex-col gap-3 border-l bg-muted/40 p-3 md:flex">{t35}{t37}</aside>;
    $[86] = t35;
    $[87] = t37;
    $[88] = t38;
  } else {
    t38 = $[88];
  }
  let t39;
  if ($[89] !== t33 || $[90] !== t38) {
    t39 = <div className="flex min-h-0 flex-1">{t33}{t38}</div>;
    $[89] = t33;
    $[90] = t38;
    $[91] = t39;
  } else {
    t39 = $[91];
  }
  let t40;
  if ($[92] !== t) {
    t40 = t("ideas.picker.selectedCount");
    $[92] = t;
    $[93] = t40;
  } else {
    t40 = $[93];
  }
  let t41;
  if ($[94] !== draft.length || $[95] !== t) {
    t41 = t("ideas.picker.count", {
      count: draft.length
    });
    $[94] = draft.length;
    $[95] = t;
    $[96] = t41;
  } else {
    t41 = $[96];
  }
  let t42;
  if ($[97] !== t41) {
    t42 = <b>{t41}</b>;
    $[97] = t41;
    $[98] = t42;
  } else {
    t42 = $[98];
  }
  let t43;
  if ($[99] !== t40 || $[100] !== t42) {
    t43 = <span className="text-sm">{t40} {t42}</span>;
    $[99] = t40;
    $[100] = t42;
    $[101] = t43;
  } else {
    t43 = $[101];
  }
  let t44;
  if ($[102] !== t) {
    t44 = t("ideas.picker.cancel");
    $[102] = t;
    $[103] = t44;
  } else {
    t44 = $[103];
  }
  let t45;
  if ($[104] !== onClose || $[105] !== t44) {
    t45 = <Button variant="secondary" onClick={onClose} className="w-[131px]">{t44}</Button>;
    $[104] = onClose;
    $[105] = t44;
    $[106] = t45;
  } else {
    t45 = $[106];
  }
  const t46 = draft.length === 0 && selectedIds.length === 0;
  let t47;
  if ($[107] !== draft || $[108] !== onConfirm || $[109] !== summaryOf) {
    t47 = () => onConfirm(draft, draft.map(id_2 => summaryOf(id_2)).filter(_temp6));
    $[107] = draft;
    $[108] = onConfirm;
    $[109] = summaryOf;
    $[110] = t47;
  } else {
    t47 = $[110];
  }
  let t48;
  if ($[111] !== t) {
    t48 = t("ideas.picker.add");
    $[111] = t;
    $[112] = t48;
  } else {
    t48 = $[112];
  }
  let t49;
  if ($[113] !== t46 || $[114] !== t47 || $[115] !== t48) {
    t49 = <Button className="w-[131px]" disabled={t46} onClick={t47}>{t48}</Button>;
    $[113] = t46;
    $[114] = t47;
    $[115] = t48;
    $[116] = t49;
  } else {
    t49 = $[116];
  }
  let t50;
  if ($[117] !== t43 || $[118] !== t45 || $[119] !== t49) {
    t50 = <DialogFooter className="items-center gap-3 border-t px-3 py-3 sm:justify-end">{t43}{t45}{t49}</DialogFooter>;
    $[117] = t43;
    $[118] = t45;
    $[119] = t49;
    $[120] = t50;
  } else {
    t50 = $[120];
  }
  let t51;
  if ($[121] !== t18 || $[122] !== t39 || $[123] !== t50) {
    t51 = <DialogContent className="flex h-[min(779px,calc(100vh-2rem))] w-[min(1166px,calc(100vw-2rem))] max-w-none flex-col gap-0 overflow-hidden p-0 sm:max-w-none">{t18}{t39}{t50}</DialogContent>;
    $[121] = t18;
    $[122] = t39;
    $[123] = t50;
    $[124] = t51;
  } else {
    t51 = $[124];
  }
  let t52;
  if ($[125] !== open || $[126] !== t16 || $[127] !== t51) {
    t52 = <Dialog open={open} onOpenChange={t16}>{t51}</Dialog>;
    $[125] = open;
    $[126] = t16;
    $[127] = t51;
    $[128] = t52;
  } else {
    t52 = $[128];
  }
  return t52;
}

// Предпросмотр — как увидит покупатель (кадр 07), в теме консоли.
function _temp6(p_3) {
  return !!p_3;
}
function _temp5(s) {
  return <option key={s.id} value={s.id}>{s.name}</option>;
}
function _temp4(p) {
  return p !== null;
}
function _temp3(id) {
  return api.products.get(id).catch(_temp2);
}
function _temp2() {
  return null;
}
export function IdeaPreview(t0) {
  const $ = _c(58);
  const {
    title,
    description,
    images,
    products,
    discount
  } = t0;
  const {
    t
  } = useTranslation();
  const [active, setActive] = useState(0);
  let t1;
  if ($[0] !== products) {
    t1 = () => products.map(_temp7);
    $[0] = products;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [checked, setChecked] = useState(t1);
  const shown = images[active] ?? images[0];
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  if ($[2] !== active || $[3] !== checked || $[4] !== description || $[5] !== discount || $[6] !== images || $[7] !== products || $[8] !== shown || $[9] !== t || $[10] !== title) {
    let t9;
    if ($[18] !== checked) {
      t9 = p_0 => checked.includes(p_0.id);
      $[18] = checked;
      $[19] = t9;
    } else {
      t9 = $[19];
    }
    const picked = products.filter(t9);
    const sum = picked.reduce(_temp8, 0);
    const all = picked.length === products.length && products.length > 0;
    const total = all ? applyGroupDiscount(sum, discount) : sum;
    t7 = "grid gap-5 lg:grid-cols-2";
    let t10;
    if ($[20] !== shown) {
      t10 = shown && <img src={shown.url} alt="" className="w-full object-cover" />;
      $[20] = shown;
      $[21] = t10;
    } else {
      t10 = $[21];
    }
    let t11;
    if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = <Heart className="absolute right-4 top-4 size-6 text-white" strokeWidth={1.5} />;
      $[22] = t11;
    } else {
      t11 = $[22];
    }
    let t12;
    if ($[23] !== t10) {
      t12 = <div className="relative overflow-hidden rounded-md bg-muted">{t10}{t11}</div>;
      $[23] = t10;
      $[24] = t12;
    } else {
      t12 = $[24];
    }
    let t13;
    if ($[25] !== active || $[26] !== images) {
      t13 = images.length > 1 && <div className="grid grid-cols-4 gap-1.5">{images.map((img, i) => <button key={img.id} type="button" onClick={() => setActive(i)} className={cn("overflow-hidden rounded-md", i !== active && "opacity-80")}><img src={img.url} alt="" className="aspect-[3/4] w-full object-cover" /></button>)}</div>;
      $[25] = active;
      $[26] = images;
      $[27] = t13;
    } else {
      t13 = $[27];
    }
    if ($[28] !== t12 || $[29] !== t13) {
      t8 = <div className="flex flex-col gap-1.5">{t12}{t13}</div>;
      $[28] = t12;
      $[29] = t13;
      $[30] = t8;
    } else {
      t8 = $[30];
    }
    t2 = "flex flex-col gap-2";
    let t14;
    if ($[31] !== t || $[32] !== title) {
      t14 = title || t("ideas.preview.untitled");
      $[31] = t;
      $[32] = title;
      $[33] = t14;
    } else {
      t14 = $[33];
    }
    if ($[34] !== t14) {
      t3 = <h2 className="text-xl font-semibold">{t14}</h2>;
      $[34] = t14;
      $[35] = t3;
    } else {
      t3 = $[35];
    }
    if ($[36] !== description) {
      t4 = description && <p className="whitespace-pre-line text-sm">{description}</p>;
      $[36] = description;
      $[37] = t4;
    } else {
      t4 = $[37];
    }
    let t15;
    if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
      t15 = <Heart className="size-6" strokeWidth={1.5} />;
      $[38] = t15;
    } else {
      t15 = $[38];
    }
    let t16;
    if ($[39] !== t) {
      t16 = t("ideas.preview.favorite");
      $[39] = t;
      $[40] = t16;
    } else {
      t16 = $[40];
    }
    let t17;
    if ($[41] !== t16) {
      t17 = <span className="flex items-center gap-2 text-sm font-medium">{t15}{t16}</span>;
      $[41] = t16;
      $[42] = t17;
    } else {
      t17 = $[42];
    }
    let t18;
    let t19;
    let t20;
    if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
      t18 = <Download className="size-6" strokeWidth={1.5} />;
      t19 = <Share className="size-6" strokeWidth={1.5} />;
      t20 = <EyeOff className="size-6" strokeWidth={1.5} />;
      $[43] = t18;
      $[44] = t19;
      $[45] = t20;
    } else {
      t18 = $[43];
      t19 = $[44];
      t20 = $[45];
    }
    if ($[46] !== t17) {
      t5 = <div className="mt-2 flex items-center gap-6">{t17}{t18}{t19}{t20}</div>;
      $[46] = t17;
      $[47] = t5;
    } else {
      t5 = $[47];
    }
    t6 = products.length > 0 && <div className="mt-5 flex flex-col gap-3 rounded-xl bg-muted/60 p-4"><h3 className="font-medium">{t("ideas.preview.products")}</h3>{products.map(p_2 => <div key={p_2.id} className="relative flex w-full max-w-[351px] gap-3 rounded-xl bg-background p-2"><span className="relative h-[123px] w-[100px] shrink-0 overflow-hidden rounded-lg bg-muted">{p_2.photo && <img src={p_2.photo} alt="" className="size-full object-cover" />}<span className="absolute inset-x-0 bottom-1 text-center text-[11px]">{t("ideas.preview.view")}</span></span><span className="flex min-w-0 flex-1 flex-col gap-1 py-1 pr-7 text-sm"><span className="line-clamp-1">{p_2.brand || p_2.name}</span>{p_2.brand && <span className="line-clamp-1 text-[11px] text-muted-foreground">{p_2.name}</span>}<span className="font-semibold">{formatPrice(p_2.price)}</span>{p_2.sizes.length > 0 && <Select className="mt-auto h-8 w-fit text-xs" defaultValue=""><option value="">{t("ideas.preview.chooseSize")}</option>{p_2.sizes.map(_temp9)}</Select>}</span><button type="button" role="checkbox" aria-checked={checked.includes(p_2.id)} aria-label={p_2.name} onClick={() => setChecked(c => c.includes(p_2.id) ? c.filter(x => x !== p_2.id) : [...c, p_2.id])} className={cn("absolute right-3 top-3 flex size-5 items-center justify-center rounded-full border", checked.includes(p_2.id) ? "border-primary bg-primary text-primary-foreground" : "bg-background")}>{checked.includes(p_2.id) && <Check className="size-3" strokeWidth={3} />}</button></div>)}<Button size="lg" className="mt-1 w-full max-w-[268px]" disabled={picked.length === 0}><ShoppingBag className="size-5" />{t("ideas.preview.buy", {
          count: picked.length,
          total: formatPrice(total)
        })}</Button>{discount && <p className="text-xs text-muted-foreground">{t("ideas.preview.discountNote", {
          value: discount.unit === "percent" ? `${discount.value}%` : formatPrice(discount.value)
        })}</p>}</div>;
    $[2] = active;
    $[3] = checked;
    $[4] = description;
    $[5] = discount;
    $[6] = images;
    $[7] = products;
    $[8] = shown;
    $[9] = t;
    $[10] = title;
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
    $[17] = t8;
  } else {
    t2 = $[11];
    t3 = $[12];
    t4 = $[13];
    t5 = $[14];
    t6 = $[15];
    t7 = $[16];
    t8 = $[17];
  }
  let t9;
  if ($[48] !== t2 || $[49] !== t3 || $[50] !== t4 || $[51] !== t5 || $[52] !== t6) {
    t9 = <div className={t2}>{t3}{t4}{t5}{t6}</div>;
    $[48] = t2;
    $[49] = t3;
    $[50] = t4;
    $[51] = t5;
    $[52] = t6;
    $[53] = t9;
  } else {
    t9 = $[53];
  }
  let t10;
  if ($[54] !== t7 || $[55] !== t8 || $[56] !== t9) {
    t10 = <div className={t7}>{t8}{t9}</div>;
    $[54] = t7;
    $[55] = t8;
    $[56] = t9;
    $[57] = t10;
  } else {
    t10 = $[57];
  }
  return t10;
}
function _temp9(s_0) {
  return <option key={s_0} value={s_0}>{s_0}</option>;
}
function _temp8(s, p_1) {
  return s + p_1.price;
}
function _temp7(p) {
  return p.id;
}